import type { Target } from "api/types/aba";

// ---------------------------------------------------------------------------
// L-Model enums
// ---------------------------------------------------------------------------

export type L1Dimension = "frequency" | "duration" | "latency" | "magnitude";

export type L2Method =
  | "frequency-count"
  | "start-stop-recording"
  | "latency-recording"
  | "anchored-rating-scale";

export type L3Context = "discrete-trial" | "net" | "task-analysis" | "free-operant";

export type TargetType = "skill-acquisition" | "behavior-reduction";

export type Phase = "baseline" | "intervention" | "generalization" | "maintenance";

export type TargetStatus = "not-active" | "in-progress" | "on-hold" | "met" | "discontinued";

export type PrimaryImplementer = "clinician" | "caregiver" | "both";

export type ImplementationSetting = "home" | "community" | "school";

export type ChainingType = "forward" | "backward" | "total-task";
export type StepScoringType = "binary" | "prompt-hierarchy";

// ---------------------------------------------------------------------------
// L2 rating-scale sub-config
// ---------------------------------------------------------------------------

export const RATING_SCALE_LEVELS = [0, 1, 2, 3, 4, 5] as const;

export interface RatingScalePoint {
  /** 0..5 */
  level: number;
  title: string;
  description: string;
  placeholder: string;
}

export interface RatingScaleConfig {
  rating_frequency: "daily" | "once-per-session" | "per-activity";
  anchors: RatingScalePoint[];
}

/** Suggested anchor titles per scale point — clinicians can edit them. */
const DEFAULT_RATING_SCALE_PLACEHOLDER: Record<number, string> = {
  0: "Did not occur",
  1: "Needs significant support",
  2: "Needs moderate support",
  3: "Needs some support",
  4: "Mostly independent",
  5: "Fully independent",
};

export const createEmptyRatingScale = (): RatingScaleConfig => ({
  rating_frequency: "once-per-session",
  anchors: RATING_SCALE_LEVELS.map((level) => ({
    level,
    title: "",
    description: "",
    placeholder: DEFAULT_RATING_SCALE_PLACEHOLDER[level as number],
  })),
});

/** Adds the zero anchor to legacy five-point configs without dropping data. */
export const normalizeRatingScaleConfig = (
  config: RatingScaleConfig | null | undefined
): RatingScaleConfig => {
  const defaults = createEmptyRatingScale();
  if (!config || !Array.isArray(config.anchors)) return defaults;

  const existingByLevel = new Map(config.anchors.map((anchor) => [anchor.level, anchor]));
  return {
    rating_frequency: config.rating_frequency ?? defaults.rating_frequency,
    anchors: RATING_SCALE_LEVELS.map((level) => {
      const existing = existingByLevel.get(level);
      return existing
        ? { ...defaults.anchors[level], ...existing, level }
        : defaults.anchors[level];
    }),
  };
};

// ---------------------------------------------------------------------------
// Teaching procedure sub-configs (strategy)
// ---------------------------------------------------------------------------

export interface PromptHierarchy {
  /** Codes selected, e.g. ["I", "M", "V"] */
  levels: string[];
  /** Whether prompted trials count toward % correct */
  count_prompted_as_correct: boolean;
  /** When count_prompted_as_correct is true, the prompt level that counts toward mastery */
  mastery_prompt_level: MasteryPromptLevel | "";
  /** Preset key applied last (informational) */
  preset?: string;
}

/**
 * Common fields shared by every teaching procedure.
 * Lifted out of individual interfaces to match the dominio (`interventionStrategies`,
 * `interventionInstructions`) and rendered by `TeachingProcedureCommonFields`.
 */
export interface TeachingProcedureCommon {
  intervention_strategies: string[];
  intervention_instructions: string;
  generalization_plan: string;
  teaching_opportunities_per_session: string;
}

export interface BehaviorReductionTeachingFields {
  date_of_implementation?: string;
  operational_definition?: string;
  function?: "attention" | "escape" | "tangible" | "sensory" | "";
  assessment_used?: string;
  common_antecedents?: string;
  antecedent_strategies?: string;
  linked_replacement_behavior?: number | "";
  safety_considerations?: string;
}

export interface CaregiverTeachingFields {
  coaching_modeling_notes?: string;
  coaching_rehearsal_notes?: string;
  coaching_feedback_notes?: string;
  coaching_visual_supports_notes?: string;
  error_correction_procedure?: string;
  practice_opportunities_per_session?: string;
}

export type FirstTrialType = "standard" | "cold-probe";
export type MasteryPromptLevel = "independent" | "gestural-or-less" | "verbal-or-less" | "any";

export interface DiscreteTrialProcedure
  extends TeachingProcedureCommon,
    BehaviorReductionTeachingFields,
    CaregiverTeachingFields {
  type: "discrete-trial";
  sd_wording: string;
  correct_response: string;
  error_correction: string;
  first_trial_type: FirstTrialType | "";
  prompt_hierarchy: PromptHierarchy;
}

/**
 * Transport field for NET procedures, which can be either a 2-value counter type (occurrence vs. correct/incorrect)
 * or a 3-value FE-only mode (prompt-hierarchy). The backend infers prompt-hierarchy mode from a non-empty
 * `prompt_hierarchy` levels array instead.
 */
export type NetCounterType = "occurrence" | "correct_incorrect";

/**
 * FE-only 3-state count mode for NET procedures. `"prompt-hierarchy"` is a UI
 * selection (it renders the PromptHierarchyEditor) — it is never sent to the
 * API as a `counter_type`; the backend infers prompt-hierarchy mode from a
 * non-empty `prompt_hierarchy` levels array instead.
 */
export type NetCountMode = NetCounterType | "prompt-hierarchy";

export interface NETProcedure
  extends TeachingProcedureCommon,
    BehaviorReductionTeachingFields,
    CaregiverTeachingFields {
  type: "net";
  opportunity_definition: string;
  reinforcement_notes: string;
  prompt_hierarchy: PromptHierarchy;
  /** FE 3-state count mode; mapped to the 2-value transport field on submit. */
  counter_type?: NetCountMode;
}

export interface TaskAnalysisProcedure
  extends TeachingProcedureCommon,
    BehaviorReductionTeachingFields,
    CaregiverTeachingFields {
  type: "task-analysis";
  prompt_hierarchy: PromptHierarchy;
  chaining: ChainingType | "";
  steps: string[];
  step_scoring: StepScoringType | "";
  step_advancement_criteria: string;
}

export interface FreeOperantProcedure
  extends TeachingProcedureCommon,
    BehaviorReductionTeachingFields,
    CaregiverTeachingFields {
  type: "free-operant";
  operational_definition: string;
  typical_contexts: string;
  antecedent_conditions: string;
  response_consequence_notes: string;
}

export type TeachingProcedure =
  | DiscreteTrialProcedure
  | NETProcedure
  | TaskAnalysisProcedure
  | FreeOperantProcedure;

/**
 * Resolves the effective NET count mode from the form's teaching procedure.
 * Precedence: a non-empty prompt hierarchy always wins — mirrors the
 * backend's own `derivePrimitiveType`, which checks for a configured
 * hierarchy before ever consulting `counter_type` (has no effect once
 * `prompt_hierarchy` is set). This also self-heals records written with both
 * a non-empty `prompt_hierarchy` AND a stale `counter_type`. Without a hierarchy, the
 * explicit `counter_type` is used, falling back to the per-dimension default
 * for legacy targets that predate the field entirely.
 */
export function getNetCountMode(
  procedure: TeachingProcedure | null | undefined,
  l1Dimension: L1Dimension | "" = "",
  targetType?: TargetType | ""
): NetCountMode {
  // Behavior-reduction targets never use prompt hierarchy in NET mode.
  // - latency+NET is always C/I
  // - frequency+NET supports occurrence or C/I, but never PH
  if (targetType === "behavior-reduction" && l1Dimension === "latency") {
    return "correct_incorrect";
  }

  const defaultMode: NetCountMode = l1Dimension === "latency" ? "correct_incorrect" : "occurrence";
  if (procedure?.type !== "net") return defaultMode;
  if ((procedure.prompt_hierarchy?.levels?.length ?? 0) > 0) {
    return targetType === "behavior-reduction" ? "correct_incorrect" : "prompt-hierarchy";
  }
  if (procedure.counter_type) {
    if (targetType === "behavior-reduction" && procedure.counter_type === "prompt-hierarchy") {
      return "correct_incorrect";
    }
    // Latency never counts occurrences — normalize a stale "occurrence" left
    // over from an L1 switch to the latency default.
    return l1Dimension === "latency" && procedure.counter_type === "occurrence"
      ? "correct_incorrect"
      : procedure.counter_type;
  }
  return defaultMode;
}

export function isCounterOccurrencePrimitive(
  l1Dimension: L1Dimension | "",
  l3Context: L3Context | "",
  procedure?: TeachingProcedure | null,
  targetType?: TargetType | ""
): boolean {
  if (l1Dimension !== "frequency") return false;
  if (l3Context === "free-operant") return true;
  // frequency + net counts occurrences only in the default "occurrence" mode;
  // C/I and prompt-hierarchy NET targets use percent thresholds (ANOW-6909).
  return (
    l3Context === "net" && getNetCountMode(procedure, l1Dimension, targetType) === "occurrence"
  );
}

// ---------------------------------------------------------------------------
// Criteria
// ---------------------------------------------------------------------------

export type CriteriaDirection = "above" | "below";
export type ThresholdUnit = "percent" | "seconds" | "minutes" | "rating" | "occurrences";
export type CriteriaTypeKey =
  | "baseline.to_intervention"
  | "baseline.to_generalization"
  | "mastery"
  | "regression"
  | "generalization";

export function deriveCriteriaDirection(
  l1Dimension: L1Dimension | "",
  targetType: TargetType,
  criteriaType: CriteriaTypeKey
): CriteriaDirection {
  const base: CriteriaDirection =
    criteriaType === "baseline.to_intervention" || criteriaType === "regression"
      ? "below"
      : "above";
  const isLatency = l1Dimension === "latency";
  const isBehaviorReduction = targetType === "behavior-reduction";
  const isInverted = isLatency !== isBehaviorReduction;
  return isInverted ? (base === "above" ? "below" : "above") : base;
}

export function getThresholdUiConfig(
  l1Dimension: L1Dimension | "",
  l3Context: L3Context | "" = "",
  procedure?: TeachingProcedure | null,
  targetType?: TargetType | ""
): {
  suffix: string;
  min: number;
  max?: number;
  step?: number;
  ariaLabel: string;
} {
  return getThresholdUiConfigForUnit(l1Dimension, undefined, l3Context, procedure, targetType);
}

export function getThresholdUnitOptions(
  l1Dimension: L1Dimension | "",
  l3Context: L3Context | "" = "",
  procedure?: TeachingProcedure | null,
  targetType?: TargetType | ""
): Array<{ value: ThresholdUnit; label: string }> {
  if (isCounterOccurrencePrimitive(l1Dimension, l3Context, procedure, targetType)) {
    return [];
  }

  switch (l1Dimension) {
    case "duration":
    case "latency":
      return [
        { value: "seconds", label: "sec" },
        { value: "minutes", label: "min" },
      ];
    default:
      return [];
  }
}

export function getThresholdUiConfigForUnit(
  l1Dimension: L1Dimension | "",
  unit?: ThresholdUnit | "",
  l3Context: L3Context | "" = "",
  procedure?: TeachingProcedure | null,
  targetType?: TargetType | ""
): {
  suffix: string;
  min: number;
  max?: number;
  step?: number;
  ariaLabel: string;
} {
  if (isCounterOccurrencePrimitive(l1Dimension, l3Context, procedure, targetType)) {
    return { suffix: "", min: 1, step: 1, ariaLabel: "Occurrences threshold" };
  }

  switch (l1Dimension) {
    case "duration":
      return {
        suffix: unit === "minutes" ? "min" : "sec",
        min: 0,
        ariaLabel: "Duration threshold",
      };
    case "latency":
      return {
        suffix: unit === "minutes" ? "min" : "sec",
        min: 0,
        ariaLabel: "Latency threshold",
      };
    case "magnitude":
      return { suffix: "/5", min: 1, max: 5, step: 1, ariaLabel: "Rating threshold" };
    case "frequency":
    default:
      return { suffix: "%", min: 0, max: 100, ariaLabel: "Percent threshold" };
  }
}

export function normalizeThresholdUnitForDimension(
  l1Dimension: L1Dimension | "",
  unit?: ThresholdUnit | "",
  l3Context: L3Context | "" = "",
  procedure?: TeachingProcedure | null,
  targetType?: TargetType | ""
): ThresholdUnit | "" {
  if (isCounterOccurrencePrimitive(l1Dimension, l3Context, procedure, targetType))
    return "occurrences";
  if (l1Dimension === "frequency") return "percent";
  if (l1Dimension === "magnitude") return "rating";
  if (l1Dimension === "duration" || l1Dimension === "latency") {
    return unit === "minutes" ? "minutes" : "seconds";
  }
  return "";
}

export interface ProgressCriteria {
  consecutive_points: number | "";
  percent: number | "";
  threshold_unit?: ThresholdUnit | "";
  direction: CriteriaDirection;
  notes: string;
}

export interface MaintenancePlan {
  first_month_every_n_sessions: number | "";
  next_three_months_every_n_sessions: number | "";
  notes: string;
}

export interface BaselineProgressionCriteria {
  to_intervention: ProgressCriteria;
  to_generalization: ProgressCriteria;
}

// ---------------------------------------------------------------------------
// Resource link
// ---------------------------------------------------------------------------

export interface ResourceLink {
  title: string;
  url: string;
}

// ---------------------------------------------------------------------------
// TargetFormValues
// ---------------------------------------------------------------------------

export interface TargetFormValues {
  // Section 1 — Overview
  title: string;
  target_type: TargetType;
  is_replacement_behavior: boolean;
  replacement_for_goal_id: number | "";
  eo_conditions: string;
  prompting_strategy_under_eo: string;
  competing_response_considerations: string;
  phase: Phase;
  status: TargetStatus;
  primary_implementer: PrimaryImplementer;
  implementation_setting: ImplementationSetting;

  // Section 2 — L-Model
  l1_dimension: L1Dimension | "";
  l2_method: L2Method | "";
  l1_sub_config: RatingScaleConfig | null;
  l3_context: L3Context | "";

  // Section 2.5 — Teaching procedure
  teaching_procedure: TeachingProcedure | null;

  // Section 3 — Criteria
  baseline_progression_criteria: BaselineProgressionCriteria;
  mastery_criteria: ProgressCriteria;
  regression_criteria: ProgressCriteria;
  generalization_criteria: ProgressCriteria;
  maintenance_plan: MaintenancePlan;

  // Section 4 — Resources
  resources: ResourceLink[];
}

// ---------------------------------------------------------------------------
// Initial values
// ---------------------------------------------------------------------------

const emptyCriteria: ProgressCriteria = {
  consecutive_points: "",
  percent: "",
  threshold_unit: "",
  direction: "above",
  notes: "",
};

const emptyMaintenance: MaintenancePlan = {
  first_month_every_n_sessions: "",
  next_three_months_every_n_sessions: "",
  notes: "",
};

export const TARGET_FORM_INITIAL_VALUES: TargetFormValues = {
  title: "",
  target_type: "skill-acquisition",
  is_replacement_behavior: false,
  replacement_for_goal_id: "",
  eo_conditions: "",
  prompting_strategy_under_eo: "",
  competing_response_considerations: "",
  phase: "baseline",
  status: "not-active",
  primary_implementer: "clinician",
  implementation_setting: "home",

  l1_dimension: "",
  l2_method: "",
  l1_sub_config: null,
  l3_context: "",

  teaching_procedure: null,

  baseline_progression_criteria: {
    to_intervention: { ...emptyCriteria, direction: "below" },
    to_generalization: { ...emptyCriteria, direction: "above" },
  },
  mastery_criteria: { ...emptyCriteria, direction: "above" },
  regression_criteria: { ...emptyCriteria, direction: "below" },
  generalization_criteria: { ...emptyCriteria, direction: "above" },
  maintenance_plan: emptyMaintenance,

  resources: [],
};

export const BR_FUNCTION_OPTIONS: Array<{
  value: NonNullable<BehaviorReductionTeachingFields["function"]>;
  label: string;
}> = [
  { value: "attention", label: "Attention" },
  { value: "escape", label: "Escape" },
  { value: "tangible", label: "Tangible" },
  { value: "sensory", label: "Sensory" },
];

// ---------------------------------------------------------------------------
// Option lists (UI)
// ---------------------------------------------------------------------------

export const TARGET_TYPE_OPTIONS: Array<{
  value: TargetType;
  label: string;
  description: string;
}> = [
  { value: "skill-acquisition", label: "Skill Acquisition", description: "Build a new skill" },
  {
    value: "behavior-reduction",
    label: "Behavior Reduction",
    description: "Decrease a maladaptive behavior",
  },
];

export const PHASE_OPTIONS: Array<{ value: Phase; label: string }> = [
  { value: "baseline", label: "Baseline" },
  { value: "intervention", label: "Intervention" },
  { value: "generalization", label: "Generalization" },
  { value: "maintenance", label: "Maintenance" },
];

export const STATUS_OPTIONS: Array<{ value: TargetStatus; label: string }> = [
  { value: "not-active", label: "Not Active" },
  { value: "in-progress", label: "In Progress" },
  { value: "on-hold", label: "On Hold" },
  { value: "met", label: "Met" },
  { value: "discontinued", label: "Discontinued" },
];

export const PRIMARY_IMPLEMENTER_OPTIONS: Array<{ value: PrimaryImplementer; label: string }> = [
  { value: "clinician", label: "Clinician" },
  { value: "caregiver", label: "Caregiver" },
  { value: "both", label: "Both" },
];

export const IMPLEMENTATION_SETTING_OPTIONS: Array<{
  value: ImplementationSetting;
  label: string;
}> = [
  { value: "home", label: "Home" },
  { value: "community", label: "Community" },
  { value: "school", label: "School" },
];

// L1
export const L1_OPTIONS: Array<{ value: L1Dimension; label: string; description: string }> = [
  {
    value: "frequency",
    label: "Happens or does not (countable)",
    description:
      "Discrete occurrences that can be counted - the behavior either occurs or it doesn't",
  },
  {
    value: "duration",
    label: "Lasts over time",
    description: "Behavior that persists for a measurable duration - how long it lasts matters",
  },
  {
    value: "latency",
    label: "Time to respond matters",
    description:
      "Time between instruction/opportunity and response - the latency is what we measure",
  },
  {
    value: "magnitude",
    label: "Varies in intensity or quality",
    description: "Quality, severity, or intensity that varies on a scale - not just yes/no",
  },
];

// L2 options, filtered by L1 — 1:1 with L1 per dominio
export const L2_OPTIONS_BY_L1: Record<
  L1Dimension,
  Array<{ value: L2Method; label: string; description: string }>
> = {
  frequency: [
    {
      value: "frequency-count",
      label: "Event-based Observation (per opportunity or occurrence)",
      description:
        "Each response or occurrence is recorded when it happens, with response details captured per event.",
    },
  ],
  duration: [
    {
      value: "start-stop-recording",
      label: "Start/stop timer for each occurrence",
      description: "Begin timing when behavior starts, stop when it ends - measures each bout",
    },
  ],
  latency: [
    {
      value: "latency-recording",
      label: "Time from instruction to response",
      description: "Measure the delay between presenting the SD and the learner's response",
    },
  ],
  magnitude: [
    {
      value: "anchored-rating-scale",
      label: "Rate on a 0-5 scale with defined anchors",
      description: "Use operationally defined scale points to rate quality or intensity",
    },
  ],
};

// L3 options, filtered by L2.
// Labels and descriptions are context-specific to the L2 method — the way DTT
// is framed for "frequency-count" (discrete responses) differs from the way it
// is framed for "start-stop-recording" (duration measured within DTT), etc.
export const L3_OPTIONS_BY_L2: Record<
  L2Method,
  Array<{ value: L3Context; label: string; description: string }>
> = {
  "frequency-count": [
    {
      value: "discrete-trial",
      label: "Structured teaching (discrete trials)",
      description:
        "Discrete trials with defined SD, response expectation, and programmed consequences delivered per opportunity",
    },
    {
      value: "net",
      label: "Teaching during natural activities (NET)",
      description:
        "Discrete responses embedded within natural activities where opportunities and reinforcement are intentionally arranged",
    },
    {
      value: "task-analysis",
      label: "Multi-step skills (task analysis)",
      description:
        "Discrete steps of a chained skill measured per step within systematic instruction",
    },
    {
      value: "free-operant",
      label: "Naturally occurring / free-operant context",
      description:
        "Discrete responses recorded as they occur naturally, without arranged instructional prompts or programmed consequences",
    },
  ],
  "start-stop-recording": [
    {
      value: "discrete-trial",
      label: "Structured teaching (discrete trials)",
      description:
        "Duration measured within a planned instructional condition with defined SD and programmed consequences",
    },
    {
      value: "net",
      label: "Teaching during natural activities (NET)",
      description:
        "Duration measured within natural activities where engagement is intentionally supported and reinforced within ongoing activity",
    },
    {
      value: "free-operant",
      label: "Naturally occurring / free-operant context (Monitoring Only)",
      description:
        "Duration measured from natural onset to offset within ongoing activity, without arranged instructional prompts or programmed consequences",
    },
  ],
  "latency-recording": [
    {
      value: "discrete-trial",
      label: "Structured teaching (discrete trials)",
      description:
        "Latency measured from a defined SD to response within planned trials with programmed consequences",
    },
    {
      value: "net",
      label: "Teaching during natural activities (NET)",
      description:
        "Latency measured from naturally embedded instructional cues within ongoing activities where reinforcement is intentionally arranged",
    },
    {
      value: "free-operant",
      label: "Naturally occurring / free-operant context (Monitoring Only)",
      description:
        "Latency measured following naturally occurring cues or environmental events, without arranged instructional prompting or programmed consequences",
    },
  ],
  "anchored-rating-scale": [
    {
      value: "discrete-trial",
      label: "Structured teaching (discrete trials)",
      description:
        "Intensity or quality evaluated within planned instructional conditions with programmed consequences",
    },
    {
      value: "net",
      label: "Teaching during natural activities (NET)",
      description:
        "Intensity or quality evaluated within natural activities where teaching and reinforcement are embedded",
    },
    {
      value: "free-operant",
      label: "Naturally occurring / free-operant context (Monitoring Only)",
      description:
        "Intensity or quality observed and rated as it occurs naturally, without arranged instructional prompting or programmed consequences",
    },
  ],
};

export const CHAINING_OPTIONS: Array<{ value: ChainingType; label: string }> = [
  { value: "total-task", label: "Total Task Presentation" },
  { value: "forward", label: "Forward Chaining" },
  { value: "backward", label: "Backward Chaining" },
];

export const STEP_SCORING_OPTIONS: Array<{ value: StepScoringType; label: string }> = [
  { value: "binary", label: "Binary (Independent/Prompted)" },
  { value: "prompt-hierarchy", label: "Full Prompt Hierarchy" },
];

/**
 * Count-type selector options for NET procedures. Frequency + NET
 * offers all three; latency + NET omits "occurrence" (no occurrence primitive
 * for latency — C/I is the default when no prompt hierarchy is selected).
 */
export const NET_COUNT_MODE_OPTIONS: Array<{
  value: NetCountMode;
  label: string;
  description: string;
}> = [
  {
    value: "occurrence",
    label: "Occurrences (raw count)",
    description: "Count each response as it happens — no correct/incorrect scoring",
  },
  {
    value: "correct_incorrect",
    label: "Correct / Incorrect",
    description: "Score each response as correct or incorrect",
  },
  {
    value: "prompt-hierarchy",
    label: "Prompt Hierarchy",
    description: "Score each response with the prompt level used",
  },
];

export const FIRST_TRIAL_TYPE_OPTIONS: Array<{ value: FirstTrialType; label: string }> = [
  { value: "standard", label: "Standard (with prompts available)" },
  { value: "cold-probe", label: "Cold Probe (no prompts, assess independent responding)" },
];

export const MASTERY_PROMPT_LEVEL_OPTIONS: Array<{ value: MasteryPromptLevel; label: string }> = [
  { value: "independent", label: "Independent only" },
  { value: "verbal-or-less", label: "Verbal or less (I, V)" },
  { value: "gestural-or-less", label: "Gestural or less (I, V, G)" },
  { value: "any", label: "Any prompt level" },
];

/**
 * Phase-transition outcome wording per the L-Model spec. Shared between the
 * criteria form (Section 3) and the read-only criteria rows so the builder,
 * preview, and detail sidebar always describe transitions identically.
 */
export const CRITERIA_OUTCOME_TEXT = {
  baselineToIntervention: "→ advance to intervention",
  baselineToGeneralization: "→ advance to generalization",
  mastery: "→ advance to next phase",
  regression: "→ return to previous phase",
  generalization: "→ advance to maintenance",
} as const;

// ---------------------------------------------------------------------------
// Intervention strategies (multi-select, target-type-scoped)
// ---------------------------------------------------------------------------

const SKILL_ACQUISITION_STRATEGIES = [
  "Naturalistic Teaching",
  "Behavior Skills Training (BST)",
  "Backward Chaining",
  "Forward Chaining",
  "Total Task Chaining",
  "Behavioral Momentum",
  "Differential Reinforcement",
  "Reinforcement of Acquisition Behaviors",
  "Reinforcement of Replacement Behaviors",
  "Discrete Trial Teaching",
  "Minimizing Attention (while ensuring safety) for Reduction Behaviors",
  "Most to Least Prompting",
  "Least to Most Prompting",
  "Modeling",
  "Self-Management Techniques",
  "Visual Supports",
  "Video Modeling",
  "Descriptive Feedback",
  "Token Economy",
  "Prompt Fading",
] as const;

const BEHAVIOR_REDUCTION_STRATEGIES = [
  "Noncontingent Reinforcement (NCR)",
  "Environmental Modifications",
  "Demand Fading",
  "Behavioral Momentum",
  "Increasing Choice",
  "Teaching Self-Monitoring",
  "Differential Reinforcement of Other Behavior (DRO)",
  "Differential Reinforcement of Alternative Behavior (DRA)",
  "Differential Reinforcement of Incompatible Behavior (DRI)",
  "Response Cost",
  "Behavioral Contract",
] as const;

export const INTERVENTION_STRATEGY_OPTIONS_BY_TARGET_TYPE: Record<TargetType, readonly string[]> = {
  "skill-acquisition": SKILL_ACQUISITION_STRATEGIES,
  "behavior-reduction": BEHAVIOR_REDUCTION_STRATEGIES,
};

// Prompt hierarchy levels
export interface PromptLevelDef {
  code: string;
  label: string;
}

export const PROMPT_LEVELS: PromptLevelDef[] = [
  { code: "FP", label: "Full Physical" },
  { code: "PP", label: "Partial Physical" },
  { code: "M", label: "Model" },
  { code: "G", label: "Gestural" },
  { code: "V", label: "Verbal" },
  { code: "Vi", label: "Visual" },
  { code: "TD", label: "Time Delay" },
  { code: "I", label: "Independent" },
  { code: "T", label: "Textual" },
];

export interface PromptPreset {
  key: string;
  label: string;
  levels: string[];
}

export const PROMPT_PRESETS: PromptPreset[] = [
  {
    key: "least-to-most",
    label: "Least-to-Most (Standard)",
    levels: ["I", "V", "G", "M", "PP", "FP"],
  },
  { key: "most-to-least", label: "Most-to-Least", levels: ["FP", "PP", "M", "G", "V", "I"] },
  { key: "verbal-focus", label: "Verbal Focus", levels: ["I", "V", "TD"] },
  { key: "gestural-verbal", label: "Gestural + Verbal", levels: ["I", "G", "V"] },
  { key: "model-independent", label: "Model + Independent Only", levels: ["I", "M"] },
  { key: "independent-probe", label: "Independent Only (Probe)", levels: ["I"] },
];

// ---------------------------------------------------------------------------
// Type guards
// ---------------------------------------------------------------------------

export const isDiscreteTrialProcedure = (
  p: TeachingProcedure | null
): p is DiscreteTrialProcedure => p?.type === "discrete-trial";

export const isNETProcedure = (p: TeachingProcedure | null): p is NETProcedure => p?.type === "net";

export const isTaskAnalysisProcedure = (p: TeachingProcedure | null): p is TaskAnalysisProcedure =>
  p?.type === "task-analysis";

export const isFreeOperantProcedure = (p: TeachingProcedure | null): p is FreeOperantProcedure =>
  p?.type === "free-operant";

// ---------------------------------------------------------------------------
// Criteria transform helpers — form ↔ API shape
// ---------------------------------------------------------------------------

/** Backend ThresholdCriteria shape (mastery / regression / generalization). */
interface ApiThresholdCriteria {
  consecutive_data_points: number;
  threshold?: number;
  threshold_unit?: ThresholdUnit;
  percent_threshold?: number;
  value_threshold?: number;
  occurrence_threshold?: number;
  direction?: CriteriaDirection;
  additional_notes?: string;
}

/** Backend MaintenancePlan shape. */
interface ApiMaintenancePlan {
  first_month_frequency: number;
  next_three_months_frequency: number;
  additional_notes?: string;
}

/**
 * Reads the first key that holds a `number`, returning `""` (empty form value)
 * when none match. Keys are tried in order, so pass the canonical field name
 * first and any legacy fallbacks after it.
 */
function pickNumber(obj: Record<string, unknown>, ...keys: string[]): number | "" {
  for (const key of keys) {
    if (typeof obj[key] === "number") return obj[key] as number;
  }
  return "";
}

/**
 * Reads the first key that holds a `string`, returning `""` when none match.
 * Keys are tried in order (canonical first, legacy fallbacks after).
 */
function pickString(obj: Record<string, unknown>, ...keys: string[]): string {
  for (const key of keys) {
    if (typeof obj[key] === "string") return obj[key] as string;
  }
  return "";
}

/**
 * Converts the form's ProgressCriteria to the backend ThresholdCriteria shape.
 * Returns null when both numeric fields are empty (criteria not configured).
 * `direction` is a UI-only field and is NOT included in the API payload.
 */
function formCriteriaToApiCriteria(
  c: ProgressCriteria,
  l1Dimension: L1Dimension | "",
  l3Context: L3Context | "" = "",
  procedure?: TeachingProcedure | null,
  targetType?: TargetType | ""
): ApiThresholdCriteria | null {
  if (c.consecutive_points === "" && c.percent === "") return null;

  const thresholdValue = c.percent === "" ? 0 : Number(c.percent);
  const thresholdUnit: ThresholdUnit = isCounterOccurrencePrimitive(
    l1Dimension,
    l3Context,
    procedure,
    targetType
  )
    ? "occurrences"
    : l1Dimension === "frequency"
      ? "percent"
      : l1Dimension === "magnitude"
        ? "rating"
        : c.threshold_unit === "minutes"
          ? "minutes"
          : "seconds";

  return {
    consecutive_data_points: c.consecutive_points === "" ? 0 : Number(c.consecutive_points),
    threshold: thresholdValue,
    threshold_unit: thresholdUnit,
    ...(c.notes ? { additional_notes: c.notes } : {}),
  };
}

/**
 * Converts a backend ThresholdCriteria (or unknown JSON) back to ProgressCriteria.
 * Falls back to empty form values for any missing/invalid field.
 *
 * Backward-compatible read: targets created before the snake_case rename
 * (`f6383ad15`) persisted criteria in the raw form shape — `consecutive_points`,
 * `percent`, `notes`. We read the canonical fields first, then fall back to the
 * legacy field names so pre-rename targets still populate the builder/detail.
 */
function apiCriteriaToFormCriteria(
  raw: unknown,
  direction: CriteriaDirection = "above",
  l1Dimension: L1Dimension | "" = "frequency",
  l3Context: L3Context | "" = "",
  procedure?: TeachingProcedure | null,
  targetType?: TargetType | ""
): ProgressCriteria {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const resolvedDirection: CriteriaDirection =
    obj.direction === "above" || obj.direction === "below"
      ? (obj.direction as CriteriaDirection)
      : direction;
  const isCounterOccurrence = isCounterOccurrencePrimitive(
    l1Dimension,
    l3Context,
    procedure,
    targetType
  );
  const thresholdValue = isCounterOccurrence
    ? pickNumber(
        obj,
        "threshold",
        "occurrence_threshold",
        "value_threshold",
        "percent_threshold",
        "percent"
      )
    : l1Dimension === "frequency"
      ? pickNumber(obj, "threshold", "percent_threshold", "value_threshold", "percent")
      : pickNumber(obj, "threshold", "value_threshold", "percent_threshold", "percent");
  const thresholdUnit = isCounterOccurrence
    ? "occurrences"
    : l1Dimension === "frequency"
      ? "percent"
      : l1Dimension === "magnitude"
        ? "rating"
        : obj.threshold_unit === "minutes"
          ? "minutes"
          : "seconds";

  return {
    consecutive_points: pickNumber(obj, "consecutive_data_points", "consecutive_points"),
    percent: thresholdValue,
    threshold_unit: thresholdUnit,
    direction: resolvedDirection,
    notes: pickString(obj, "additional_notes", "notes"),
  };
}

/**
 * Converts the form's MaintenancePlan to the backend MaintenancePlan shape.
 * Returns null when both numeric fields are empty.
 */
function formMaintenanceToApiMaintenance(m: MaintenancePlan): ApiMaintenancePlan | null {
  if (m.first_month_every_n_sessions === "" && m.next_three_months_every_n_sessions === "") {
    return null;
  }
  return {
    first_month_frequency:
      m.first_month_every_n_sessions === "" ? 0 : Number(m.first_month_every_n_sessions),
    next_three_months_frequency:
      m.next_three_months_every_n_sessions === ""
        ? 0
        : Number(m.next_three_months_every_n_sessions),
    ...(m.notes ? { additional_notes: m.notes } : {}),
  };
}

/**
 * Converts a backend MaintenancePlan (or unknown JSON) back to the form MaintenancePlan.
 *
 * Backward-compatible read: pre-rename targets (`f6383ad15`) persisted the plan
 * in the raw form shape — `first_month_every_n_sessions`,
 * `next_three_months_every_n_sessions`, `notes`. Canonical fields are read
 * first, with the legacy field names as a fallback.
 */
function apiMaintenanceToFormMaintenance(raw: unknown): MaintenancePlan {
  const obj = (raw ?? {}) as Record<string, unknown>;
  return {
    first_month_every_n_sessions: pickNumber(
      obj,
      "first_month_frequency",
      "first_month_every_n_sessions"
    ),
    next_three_months_every_n_sessions: pickNumber(
      obj,
      "next_three_months_frequency",
      "next_three_months_every_n_sessions"
    ),
    notes: pickString(obj, "additional_notes", "notes"),
  };
}

// ---------------------------------------------------------------------------
// Rating-scale anchor transform helpers — form ↔ API shape
// ---------------------------------------------------------------------------

/**
 * Flattens the form's RatingScaleConfig (array of anchors) to the backend
 * flat-field format: anchor_0 / anchor_0_definition ... anchor_5 / anchor_5_definition.
 */
function flattenRatingScaleConfig(cfg: RatingScaleConfig): Record<string, unknown> {
  const normalized = normalizeRatingScaleConfig(cfg);
  const result: Record<string, unknown> = { rating_frequency: normalized.rating_frequency };
  for (const anchor of normalized.anchors) {
    result[`anchor_${anchor.level}`] = anchor.title;
    result[`anchor_${anchor.level}_definition`] = anchor.description;
  }
  return result;
}

/**
 * Expands a backend flat-field rating-scale config back into the form's
 * RatingScaleConfig shape (anchors array). Handles targets stored with the
 * flat format (`anchor_0`, `anchor_0_definition`, …).
 */
function expandRatingScaleConfig(raw: unknown): RatingScaleConfig {
  const obj = (raw ?? {}) as Record<string, unknown>;
  const rawAnchors = Array.isArray(raw) ? raw : Array.isArray(obj.anchors) ? obj.anchors : [];
  const anchorsByLevel = new Map<number, Record<string, unknown>>();
  rawAnchors.forEach((value, index) => {
    if (!value || typeof value !== "object") return;
    const anchor = value as Record<string, unknown>;
    const level = Number(anchor.level ?? anchor.value ?? index);
    if (Number.isInteger(level)) anchorsByLevel.set(level, anchor);
  });

  return {
    rating_frequency:
      (obj.rating_frequency as RatingScaleConfig["rating_frequency"]) ?? "once-per-session",
    anchors: RATING_SCALE_LEVELS.map((level) => {
      const anchor = anchorsByLevel.get(level);
      const title =
        anchor?.title ?? anchor?.label ?? obj[`anchor_${level}`] ?? obj[`anchor${level}`];
      const definition =
        anchor?.description ??
        anchor?.definition ??
        obj[`anchor_${level}_definition`] ??
        obj[`anchor${level}_definition`];

      return {
        level,
        title: typeof title === "string" ? title : "",
        description: typeof definition === "string" ? definition : "",
        placeholder: DEFAULT_RATING_SCALE_PLACEHOLDER[level] ?? "",
      };
    }),
  };
}

// ---------------------------------------------------------------------------
// API → form values mapper
// ---------------------------------------------------------------------------

const VALID_SETTINGS = new Set(IMPLEMENTATION_SETTING_OPTIONS.map((o) => o.value));
const VALID_L2: ReadonlySet<TargetFormValues["l2_method"]> = new Set([
  "frequency-count",
  "start-stop-recording",
  "latency-recording",
  "anchored-rating-scale",
] as const);
const VALID_L3: ReadonlySet<TargetFormValues["l3_context"]> = new Set([
  "discrete-trial",
  "net",
  "task-analysis",
  "free-operant",
] as const);
// Legacy frequency-family L2 values now collapsed to a single 1:1 mapping.
const LEGACY_L2_TO_FREQUENCY_COUNT = new Set([
  "partial-interval",
  "whole-interval",
  "momentary-time-sampling",
]);

function enforceNoPromptHierarchyOnFormProcedure(
  procedure: NonNullable<TargetFormValues["teaching_procedure"]>
): void {
  if (
    procedure.type !== "discrete-trial" &&
    procedure.type !== "net" &&
    procedure.type !== "task-analysis"
  ) {
    return;
  }

  procedure.prompt_hierarchy = {
    ...procedure.prompt_hierarchy,
    levels: [],
    count_prompted_as_correct: false,
    mastery_prompt_level: "independent",
  };

  if (procedure.type === "task-analysis") {
    procedure.step_scoring = "binary";
  }
}

function enforceNoPromptHierarchyOnTransport(
  transport: Record<string, unknown>,
  options?: { forceBinaryStepScoring?: boolean }
): void {
  transport.prompt_hierarchy = [];
  transport.count_prompted_as_correct = false;
  delete transport.mastery_prompt_level;
  if (options?.forceBinaryStepScoring) {
    transport.step_scoring = "binary";
  }
}

/**
 * Maps an API `Target` to the normalized `TargetFormValues` shape. Used to
 * pre-populate the builder in edit mode and to render the target-detail
 * sidebar with the same components as the builder preview. Normalizes legacy
 * values (old L2 codes, missing procedure common fields) along the way.
 */
export function targetToFormValues(target: Target): TargetFormValues {
  const procedure = (target.step35_config as any) || null;
  const baseline = (target.baseline_progression_criteria as any) || {};
  const rawSetting = target.implementation_setting as TargetFormValues["implementation_setting"];
  const implementationSetting = VALID_SETTINGS.has(rawSetting) ? rawSetting : "home";

  const rawL2 = target.l2_method as string | null;
  const l2Method: TargetFormValues["l2_method"] = rawL2
    ? LEGACY_L2_TO_FREQUENCY_COUNT.has(rawL2)
      ? "frequency-count"
      : VALID_L2.has(rawL2 as TargetFormValues["l2_method"])
        ? (rawL2 as TargetFormValues["l2_method"])
        : ""
    : "";

  const rawL3 = target.l3_context as string | null;
  const l3Context: TargetFormValues["l3_context"] =
    rawL3 && VALID_L3.has(rawL3 as TargetFormValues["l3_context"])
      ? (rawL3 as TargetFormValues["l3_context"])
      : "";

  const rawTeachingProcedure: TargetFormValues["teaching_procedure"] =
    procedure?.type &&
    ["discrete-trial", "net", "task-analysis", "free-operant"].includes(procedure.type)
      ? (() => {
          const base = {
            ...procedure,
            // Backfill common fields for legacy targets created before
            // intervention strategies/instructions were lifted to the common shape.
            intervention_strategies: Array.isArray(procedure.intervention_strategies)
              ? procedure.intervention_strategies
              : [],
            intervention_instructions:
              typeof procedure.intervention_instructions === "string"
                ? procedure.intervention_instructions
                : "",
            linked_replacement_behavior:
              typeof procedure.linked_replacement_behavior === "number"
                ? procedure.linked_replacement_behavior
                : "",
            practice_opportunities_per_session:
              procedure.practice_opportunities_per_session != null
                ? String(procedure.practice_opportunities_per_session)
                : "",
          };

          // For procedures that support prompt hierarchies, reconstruct the FE
          // object form from either the canonical transport shape (array + root flags)
          // or the legacy FE-stored object shape { levels, count_prompted_as_correct, ... }.
          if (
            procedure.type === "discrete-trial" ||
            procedure.type === "net" ||
            procedure.type === "task-analysis"
          ) {
            const ph = procedure.prompt_hierarchy;
            let hierarchyObj: PromptHierarchy;

            if (Array.isArray(ph)) {
              // Transport form (array) — reconstruct object form for the FE form state
              hierarchyObj = {
                levels: ph as string[],
                count_prompted_as_correct: procedure.count_prompted_as_correct ?? false,
                mastery_prompt_level: procedure.mastery_prompt_level ?? "independent",
              };
            } else if (ph && typeof ph === "object" && !Array.isArray(ph)) {
              // Already object form (pre-normalization FE-stored shape)
              hierarchyObj = {
                levels: Array.isArray((ph as any).levels) ? (ph as any).levels : [],
                count_prompted_as_correct: (ph as any).count_prompted_as_correct ?? false,
                mastery_prompt_level: (ph as any).mastery_prompt_level ?? "independent",
              };
            } else {
              hierarchyObj = {
                levels: [],
                count_prompted_as_correct: false,
                mastery_prompt_level: "independent",
              };
            }

            // Strip root-level flag fields from the spread — they live inside hierarchyObj now.
            const restBase = { ...base };
            delete (restBase as any).count_prompted_as_correct;
            delete (restBase as any).mastery_prompt_level;
            return { ...restBase, prompt_hierarchy: hierarchyObj } as unknown as NonNullable<
              TargetFormValues["teaching_procedure"]
            >;
          }

          return base;
        })()
      : null;

  const l1Dimension = (target.l1_dimension as TargetFormValues["l1_dimension"]) ?? "";
  const targetType = (target.target_type as TargetFormValues["target_type"]) ?? "skill-acquisition";

  // Materialize the NET count mode so the builder's selector reflects the
  // effective state: explicit counter_type wins, legacy targets with a
  // configured prompt hierarchy load as "prompt-hierarchy", the rest fall
  // back to the per-dimension default (see getNetCountMode). Built as a new
  // object rather than mutating `rawTeachingProcedure` in place.
  const teachingProcedure: TargetFormValues["teaching_procedure"] =
    rawTeachingProcedure?.type === "net"
      ? {
          ...rawTeachingProcedure,
          counter_type: getNetCountMode(rawTeachingProcedure, l1Dimension, targetType),
        }
      : rawTeachingProcedure;

  // Behavior-reduction targets never use prompt hierarchy. Normalize any
  // legacy/invalid PH-bearing state into non-PH equivalents on load.
  if (targetType === "behavior-reduction" && teachingProcedure) {
    enforceNoPromptHierarchyOnFormProcedure(teachingProcedure);
    if (teachingProcedure.type === "net") {
      teachingProcedure.counter_type = getNetCountMode(teachingProcedure, l1Dimension, targetType);
    }
  }

  return {
    ...TARGET_FORM_INITIAL_VALUES,
    title: target.title ?? "",
    target_type: targetType,
    phase: (target.phase as TargetFormValues["phase"]) ?? "baseline",
    status: (target.status as TargetFormValues["status"]) ?? "not-active",
    is_replacement_behavior: target.is_replacement_behavior ?? false,
    replacement_for_goal_id:
      (target.replacement_behavior_config as any)?.replacement_for_goal_id ?? "",
    eo_conditions: (target.replacement_behavior_config as any)?.eo_conditions ?? "",
    prompting_strategy_under_eo:
      (target.replacement_behavior_config as any)?.prompting_strategy_under_eo ?? "",
    competing_response_considerations:
      (target.replacement_behavior_config as any)?.competing_response_considerations ?? "",
    primary_implementer:
      (target.primary_implementer as TargetFormValues["primary_implementer"]) ?? "clinician",
    implementation_setting: implementationSetting,
    l1_dimension: l1Dimension,
    l2_method: l2Method,
    l1_sub_config:
      target.l1_sub_config && (target.l1_dimension as string) === "magnitude"
        ? expandRatingScaleConfig(target.l1_sub_config)
        : (target.l1_sub_config as TargetFormValues["l1_sub_config"]) ?? null,
    l3_context: l3Context,
    teaching_procedure: teachingProcedure,
    baseline_progression_criteria: {
      to_intervention: apiCriteriaToFormCriteria(
        baseline.to_intervention,
        deriveCriteriaDirection(l1Dimension, targetType, "baseline.to_intervention"),
        l1Dimension,
        l3Context,
        teachingProcedure,
        targetType
      ),
      to_generalization: apiCriteriaToFormCriteria(
        baseline.to_generalization,
        deriveCriteriaDirection(l1Dimension, targetType, "baseline.to_generalization"),
        l1Dimension,
        l3Context,
        teachingProcedure,
        targetType
      ),
    },
    mastery_criteria: apiCriteriaToFormCriteria(
      target.mastery_criteria,
      deriveCriteriaDirection(l1Dimension, targetType, "mastery"),
      l1Dimension,
      l3Context,
      teachingProcedure,
      targetType
    ),
    regression_criteria: apiCriteriaToFormCriteria(
      target.regression_criteria,
      deriveCriteriaDirection(l1Dimension, targetType, "regression"),
      l1Dimension,
      l3Context,
      teachingProcedure,
      targetType
    ),
    generalization_criteria: apiCriteriaToFormCriteria(
      target.generalization_criteria,
      deriveCriteriaDirection(l1Dimension, targetType, "generalization"),
      l1Dimension,
      l3Context,
      teachingProcedure,
      targetType
    ),
    maintenance_plan: apiMaintenanceToFormMaintenance(target.maintenance_plan),
    resources: target.resources ?? [],
  };
}

// ---------------------------------------------------------------------------
// Form → API payload mapper
// ---------------------------------------------------------------------------

export function formValuesToTargetPayload(values: TargetFormValues): Partial<Target> {
  return {
    title: values.title.trim(),
    target_type: values.target_type as Target["target_type"],
    phase: values.phase as Target["phase"],
    status: values.status as Target["status"],
    is_replacement_behavior: values.is_replacement_behavior,
    replacement_behavior_config: values.is_replacement_behavior
      ? {
          replacement_for_goal_id: values.replacement_for_goal_id || null,
          eo_conditions: values.eo_conditions,
          prompting_strategy_under_eo: values.prompting_strategy_under_eo,
          competing_response_considerations: values.competing_response_considerations,
        }
      : {},
    l1_dimension: values.l1_dimension || null,
    // l2_method is intentionally omitted from the payload — the backend computes
    // it server-side from `l1_dimension` and ignores it on input.
    l1_sub_config:
      values.l1_sub_config && values.l1_dimension === "magnitude"
        ? flattenRatingScaleConfig(values.l1_sub_config)
        : values.l1_sub_config ?? {},
    l3_context: values.l3_context || null,
    step35_config: (() => {
      const proc = values.teaching_procedure;
      if (!proc) return {};

      const normalizedProc = {
        ...proc,
        linked_replacement_behavior:
          proc.linked_replacement_behavior === "" || proc.linked_replacement_behavior == null
            ? undefined
            : Number(proc.linked_replacement_behavior),
        practice_opportunities_per_session:
          proc.practice_opportunities_per_session === "" ||
          proc.practice_opportunities_per_session == null
            ? undefined
            : Number(proc.practice_opportunities_per_session),
      } as TeachingProcedure;

      // For procedure types that support prompt hierarchies, normalize the FE
      // object form to the canonical transport shape:
      //   prompt_hierarchy.levels         → prompt_hierarchy (array)
      //   prompt_hierarchy.count_prompted_as_correct → count_prompted_as_correct (root)
      //   prompt_hierarchy.mastery_prompt_level      → mastery_prompt_level (root)
      if (
        normalizedProc.type === "discrete-trial" ||
        normalizedProc.type === "net" ||
        normalizedProc.type === "task-analysis"
      ) {
        const { prompt_hierarchy, ...rest } = normalizedProc as any;
        const transport =
          prompt_hierarchy &&
          typeof prompt_hierarchy === "object" &&
          !Array.isArray(prompt_hierarchy)
            ? {
                ...rest,
                prompt_hierarchy: Array.isArray(prompt_hierarchy.levels)
                  ? prompt_hierarchy.levels
                  : [],
                count_prompted_as_correct: prompt_hierarchy.count_prompted_as_correct === true,
                ...(prompt_hierarchy.mastery_prompt_level
                  ? { mastery_prompt_level: prompt_hierarchy.mastery_prompt_level }
                  : {}),
              }
            : // Already transport form (array) or absent prompt_hierarchy
              { ...(normalizedProc as any) };

        // NET count mode → transport fields.
        // PH mode is expressed purely via a non-empty `prompt_hierarchy` array
        // — `counter_type` is omitted entirely so a reload doesn't misread the
        // target as occurrence/C-I mode. `getNetCountMode` gives the levels
        // array precedence over `counter_type` on read-back, so we omit
        // `counter_type` whenever levels are non-empty to preserve PH mode on
        // reload.
        if (normalizedProc.type === "net") {
          delete transport.counter_type;
          const l1 = values.l1_dimension;
          if (l1 === "frequency" || l1 === "latency") {
            const mode = getNetCountMode(values.teaching_procedure, l1, values.target_type);
            if (mode !== "prompt-hierarchy") {
              transport.prompt_hierarchy = [];
              if (l1 === "frequency") transport.counter_type = mode;
            }
          }
        }

        if (values.target_type === "behavior-reduction") {
          enforceNoPromptHierarchyOnTransport(transport, {
            forceBinaryStepScoring: normalizedProc.type === "task-analysis",
          });
        }

        return transport;
      }

      return normalizedProc;
    })(),
    primary_implementer: values.primary_implementer,
    implementation_setting: values.implementation_setting,
    baseline_progression_criteria: {
      to_intervention: formCriteriaToApiCriteria(
        values.baseline_progression_criteria.to_intervention,
        values.l1_dimension,
        values.l3_context,
        values.teaching_procedure,
        values.target_type
      ),
      to_generalization: formCriteriaToApiCriteria(
        values.baseline_progression_criteria.to_generalization,
        values.l1_dimension,
        values.l3_context,
        values.teaching_procedure,
        values.target_type
      ),
    },
    mastery_criteria: formCriteriaToApiCriteria(
      values.mastery_criteria,
      values.l1_dimension,
      values.l3_context,
      values.teaching_procedure,
      values.target_type
    ),
    regression_criteria:
      formCriteriaToApiCriteria(
        values.regression_criteria,
        values.l1_dimension,
        values.l3_context,
        values.teaching_procedure,
        values.target_type
      ) ?? {},
    generalization_criteria: formCriteriaToApiCriteria(
      values.generalization_criteria,
      values.l1_dimension,
      values.l3_context,
      values.teaching_procedure,
      values.target_type
    ),
    maintenance_plan: formMaintenanceToApiMaintenance(values.maintenance_plan) ?? {},
    resources: values.resources.length > 0 ? values.resources : null,
  };
}
